<template>
  <div :class="$.filter_main">
    <filter-header
      ref="header"
      :title="title"
      :help-icon="true"
      :options="savedFilters"
      :selected-filter="selectedFilter"
      :component-name="componentName"
      :styles="{paddingBottom: '16px'}"
      @selectFilter="data => selectFilter(data)"
      @showHelpPopup="data => $emit('showHelpPopup', data)"
      @closeClick="$emit('onClose')"
      @onResetClickFromHeader="$emit('onResetClick')"
    />
    <div
      :class="$.body"
      :style="{height: height}"
    >
      <div
        ref="scroll"
        :class="[$.filter_settings, 'gantt_scroll']"
      >
        <div
          v-for="(item, index) in preparedStaticItems"
          :key="index"
          :class="$.filter_item"
        >
          <date-with-time
            v-if="item.isDateWithTime"
            :filter-items="item.filterItems"
            @changeFilter="changeFilter"
          />
          <vgp-label-slot
            v-if="item.isTab"
            :label="item.label"
            :class="$.slot"
          >
            <template #body>
              <tab-item
                :filter-items="item.tabItems"
                @changeFilter="changeFilter"
              />
            </template>
          </vgp-label-slot>
          <vgp-label-slot
            v-if="item && !item.isTab && !item.isDateWithTime && !item.hide"
            :label="withLabel(item.property) ? item.label : ''"
            :class="$.slot"
          >
            <template #body>
              <filter-item
                ref="filterItem"
                :filter-item="item"
                :readonly="false"
                @changeFilter="changeFilter"
              />
            </template>
          </vgp-label-slot>
        </div>
        <div
          v-for="(item, index) in preparedCustomColumns"
          :key="index"
          :class="$.filter_item"
        >
          <div
            v-if="item.columnsTitle"
            :class="$.columns_title"
          >
            <div :class="$.text">
              {{ item.columnsTitle }}
            </div>
            <div :class="$.line" />
          </div>
          <vgp-label-slot
            v-else
            :label="item.label"
            :class="$.slot"
          >
            <template #body>
              <filter-item
                ref="filterItem"
                :filter-item="item"
                :readonly="false"
                @changeFilter="changeCustomFieldsFilter"
              />
            </template>
          </vgp-label-slot>
        </div>
        <div
          v-if="isShowNotificationMessage"
          :style="{paddingTop: '16px'}"
        >
          <inline-notification
            type="warning"
            :text="notificationMessage"
            :message-styles="{'font-size': '12px', 'white-space': 'normal'}"
          />
        </div>
      </div>
      <gradient :show="isShowGradient" />
    </div>
    <filter-footers
      ref="footers"
      :footer-type="'footer-main'"
      @onSaveClick="$emit('onSaveClick')"
      @onResetClick="$emit('onResetClick')"
    />
  </div>
</template>

<script>
import filterHeader from '../components/filterHeader.vue';
import filterItem from '../components/filterItem.vue';
import filterFooters from '../components/filterFooters.vue';
import gradient from '../components/gradient.vue';
import vgpLabelSlot from '../../common/VueComponents/label/vgpLabelSlot.vue';
import filterConstants from '../constants';
import tabItem from '../components/filterTabItem.vue';
import filterHelpers from '../helpers';
import dateWithTime from '../components/filterDateWithTimeItem.vue';
import inlineNotification from '../../common/VueComponents/nofitication/inline/InlineNotification.vue';
import rights from '../../../components/rights/index';
import pricingHelpers from '../../../helpers/pricingHelper';

export default {
  name: 'FilterMain',
  components: {
    filterHeader, filterItem, filterFooters, gradient, vgpLabelSlot, tabItem, dateWithTime, inlineNotification,
  },
  props: {
    filter: { type: Object, required: true, default: () => {} },
    savedFilters: { type: Array, required: false, default: () => [] },
    componentName: { type: String, required: true, default: '' },
  },
  emits: {
    onClose: null,
    changeFilter: Object,
    setFilter: Object,
    onSaveClick: null,
    onResetClick: null,
    showHelpPopup: Object,
  },
  data() {
    return {
      height: 0,
      selectedFilter: [],
      notificationMessage: __('filter_baseline_notification'),
      isShowGradient: true,
    };
  },
  computed: {
    title() {
      // return __(`filter-title-${this.filter.type}`);
      return __('actual_filter');
    },
    preparedStaticItems() {
      return this.prepareStaticItemsIfNeeded();
    },
    preparedCustomColumns() {
      let items = [];

      if (this.filter.customColumnsItems && this.filter.customColumnsItems.length) {
        items = [{ columnsTitle: __('filter_custom_columns_title') }].concat(this.filter.customColumnsItems);
      }

      return items;
    },
    advancedSettings() {
      return this.$store.getters['filter/getAdvancedSettingsByFilterType'](this.filter.type);
    },
    isShowNotificationMessage() {
      if (this.advancedSettings?.isShowNotificationMessage) {
        return true;
      }

      return false;
    },
  },
  watch: {
    filter: {
      handler(filter) {
        if (filter && filter.id) {
          this.selectedFilter = this.savedFilters.find(item => item.id === filter.id);
        } else {
          this.selectedFilter = [];
        }
      },
      immediate: true,
    },
    advancedSettings() {
      const reportFilters = [filterConstants.FILTERS.reportsBudgetProject, filterConstants.FILTERS.reportsTimeProject];

      if (reportFilters.includes(this.filter.type)) {
        this.prepareStaticItemsIfNeeded();
      }
    },
    $route() {
      this.calcHeight();
    },
  },
  mounted() {
    const element = this.$refs.scroll;

    this.calcHeight();
    element.addEventListener('wheel', this.onScroll);
  },
  beforeDestroy() {
    const element = this.$refs.scroll;

    element.removeEventListener('wheel', this.onScroll);
  },
  methods: {
    withLabel(prop) {
      return prop !== filterConstants.PROPERTY.overdue;
    },
    onScroll() {
      const element = this.$refs.scroll;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight) {
        this.isShowGradient = false;
      } else {
        this.isShowGradient = true;
      }
    },
    hideFilterItemIfNedded(item) {
      if (filterConstants.PROPERTY.portfolio_id === item.property) {
        if (!rights.account.hasRight('multiview_all_features') || this.advancedSettings?.baselineEnabled) {
          return {
            ...item,
            hide: true,
          };
        }
      }

      if (filterConstants.PROPERTY.overdue === item.property) {
        if (!pricingHelpers.checkPricingAccess('overdue_tasks')) {
          return {
            ...item,
            hide: true,
          };
        }
      }

      return item;
    },
    prepareStaticItemsIfNeeded() {
      let staticItems = this.filter.staticFilterItems;

      const filtersWithProjectsTab = [
        filterConstants.FILTERS.reportsProgressMilestone,
        filterConstants.FILTERS.reportsProgressStatuses,
        filterConstants.FILTERS.reportsProgressSchedule,
        filterConstants.FILTERS.reportsProgressPortfolio,
        filterConstants.FILTERS.reportsBudgetProject,
        filterConstants.FILTERS.reportsTimeProject,
      ];
      const filtersWithTimeDateRange = [filterConstants.FILTERS.history];

      staticItems = staticItems.map(item => ({
        ...this.hideFilterItemIfNedded(item),
      }));

      if (this.filter?.type && filtersWithProjectsTab.includes(this.filter.type)) {
        staticItems = filterHelpers.prepareItemsWithTabTemplate(staticItems);
      }

      if (this.filter?.type && filtersWithTimeDateRange.includes(this.filter.type)) {
        staticItems = filterHelpers.prepareDateWithTime(staticItems);
      }

      return staticItems;
    },
    calcHeight() {
      const header = this.$refs.header.$el;
      const footer = this.$refs.footers.$el;
      const appHeader = document.documentElement.querySelector('#header');
      const currentHeader = appHeader ? appHeader.clientHeight : 94;
      const appFooter = 30;
      const diff = header.clientHeight + footer.clientHeight + currentHeader + appFooter;

      this.height = `calc(100vh - ${diff}px)`;
    },
    selectFilter(data) {
      this.selectedFilter = data;
      const filterData = data[0];

      if (filterData) {
        this.$emit('setFilter', { id: filterData.id, name: filterData.name, properties: filterData.filter });
      }
    },
    changeFilter(data) {
      this.selectedFilter = [];
      this.$emit('changeFilter', data);
    },
    changeCustomFieldsFilter(data) {
      this.selectedFilter = [];
      this.$emit('changeFilter', { customColumns: data });
    },
  },
};
</script>

<style module='$' lang='less'>
@import '../../../less/_mixins.less';
@import '../../../less/_fonts.less';

.filter_main {
  .body {
    position: relative;

    .filter_settings {
      height: inherit;
      padding: 12px;

      .filter_item {
        margin-bottom: 12px;

        .columns_title {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 7px 0;

          .text {
            white-space: nowrap;
            text-transform: uppercase;
            .FontLatoRegular;
            .fs(12px, 18px);
            color: #999999;
            margin-right: 8px;
          }

          .line {
            height: 1px;
            width: 100%;
            background: #e5e5e5;
          }
        }
      }
    }
  }
}
</style>
